import type { RootState } from '@/redux/store';
import { createSelector } from '@reduxjs/toolkit';
import { TitleEntityV2, TitleMetadataItem } from '@/redux/slices/titleV2Slice.types';
import { LoadingStatuses } from '@/constants/constants';
import { TitleEntity } from '@/redux/slices/slice_types';

export const selectTitleV2Slice = (state: RootState) => state.titleV2 || {};
export const selectRootTitleId = createSelector(selectTitleV2Slice, (state) => state.rootTitleId);
export const selectActiveTitleId = createSelector(selectTitleV2Slice, (state) => state.activeTitleId);
export const selectActiveTitleLevel = createSelector(selectTitleV2Slice, (state) => state.activeTitleLevel);
export const selectActiveTitleHierarchy = createSelector(selectTitleV2Slice, (state) => state.activeTitleHierarchy);
export const selectSelectedTitleId = createSelector(selectTitleV2Slice, (state) => state.selectedTitleId);

export const selectRootTitle = createSelector(selectTitleV2Slice, selectRootTitleId, (titleSlice, rootTitleId) =>
  rootTitleId !== null ? titleSlice.titlesById[rootTitleId] : undefined
);

export const selectActiveTitle = createSelector(selectTitleV2Slice, selectActiveTitleId, (titleSlice, activeTitleId) =>
  activeTitleId !== null ? titleSlice.titlesById[activeTitleId] : undefined
);

export const selectTitlesById = createSelector(selectTitleV2Slice, (titleSlice) => titleSlice.titlesById);

export const selectTitleV2ById = createSelector(
  selectTitleV2Slice,
  (_: RootState, id: number | undefined) => id,
  // The `id` argument can be `undefined` because in many cases, the ID may not be known at the time we need to use the `selectTitleV2ById` selector.
  // For example, the ID might come from a query parameter that is missing.
  // However, since selector hooks can't be called conditionally, we must allow `undefined` as a valid argument.
  (titleSlice, id): TitleEntityV2 | undefined => (id !== undefined ? titleSlice.titlesById[id] : undefined)
);

export const selectTitleLoadingStatusById = createSelector(
  selectTitleV2Slice,
  (_: RootState, titleId: number | undefined) => titleId,
  (titleSlice, titleId): LoadingStatuses | undefined => {
    if (titleId !== undefined) {
      return titleSlice.titleLoadingStatusById[titleId];
    }
  }
);

export const selectIsRootTitleLoading = createSelector(selectTitleV2Slice, selectRootTitleId, (titleSlice, rootTitleId) => {
  if (rootTitleId !== null) {
    return titleSlice.titleLoadingStatusById[rootTitleId] === LoadingStatuses.pending;
  }

  return false;
});

export const selectIsActiveTitleLoading = createSelector(selectTitleV2Slice, selectActiveTitleId, (titleSlice, activeTitleId) => {
  if (activeTitleId !== null) {
    return titleSlice.titleLoadingStatusById[activeTitleId] === LoadingStatuses.pending;
  }

  return false;
});

export const selectChildrenLoadingStatusByTitleId = createSelector(
  selectTitleV2Slice,
  (_: RootState, titleId: number) => titleId,
  (titleSlice, titleId): LoadingStatuses | undefined => titleSlice.childrenLoadingStatusByTitleId[titleId]
);

export const selectChildrenByTitleIdMap = createSelector(selectTitleV2Slice, (titleSlice) => titleSlice.childrenByTitleId);

export const selectChildrenByTitleId = createSelector(
  selectTitleV2Slice,
  (_: RootState, titleId: number | undefined) => titleId,
  (titleSlice, titleId): TitleEntityV2[] | undefined => (titleId !== undefined ? titleSlice.childrenByTitleId[titleId] : undefined)
);

export const selectActiveVersionsByTitleIdMap = createSelector(selectTitleV2Slice, (titleSlice) => titleSlice.activeVersionsByTitleId);

export const selectActiveVersions = createSelector(selectTitleV2Slice, (titleSlice) => titleSlice.activeVersions);

export const selectActiveTitleVersions = createSelector(
  selectTitleV2Slice,
  selectActiveTitleId,
  (titleSlice, activeTitleId): TitleEntity[] | undefined => {
    if (!activeTitleId || !titleSlice.activeVersionsByTitleId[activeTitleId]) {
      return;
    }

    return titleSlice.activeVersionsByTitleId[activeTitleId];
  }
);

export const selectActiveVersionsByTitleId = createSelector(
  selectTitleV2Slice,
  (_: RootState, titleId: number | undefined) => titleId,
  (titleSlice, titleId) => {
    if (!titleId || !titleSlice.activeVersionsByTitleId[titleId]) {
      return;
    }

    return titleSlice.activeVersionsByTitleId[titleId];
  }
);

const selectIsActiveTitleVersionsLoading = createSelector(selectTitleV2Slice, selectActiveTitleId, (titleSlice, activeTitleId) => {
  if (!activeTitleId || !titleSlice.activeVersionsLoadingStatusByTitleId[activeTitleId]) {
    return false;
  }

  return titleSlice.activeVersionsLoadingStatusByTitleId[activeTitleId] === LoadingStatuses.pending;
});

export const selectIsActiveVersionsLoadingByTitle = createSelector(
  selectTitleV2Slice,
  (_: RootState, titleId: number | undefined) => titleId,
  (titleSlice, titleId) => {
    if (!titleId || !titleSlice.activeVersionsLoadingStatusByTitleId[titleId]) {
      return false;
    }

    return titleSlice.activeVersionsLoadingStatusByTitleId[titleId] === LoadingStatuses.pending;
  }
);

export const selectIsRootTitleOrVersionsLoading = createSelector(
  selectTitleV2Slice,
  selectRootTitleId,
  selectIsActiveTitleVersionsLoading,
  (titleSlice, rootTitleId, isActiveTitleVersionsLoading) => {
    if (rootTitleId !== null) {
      return titleSlice.titleLoadingStatusById[rootTitleId] === LoadingStatuses.pending || isActiveTitleVersionsLoading;
    }

    return false;
  }
);

export const selectRootTitleIsSeries = createSelector(selectRootTitle, (rootTitle) => {
  if (!rootTitle) {
    return false;
  }
  return rootTitle.entity_type === 'series' || rootTitle.entity_type === 'campaign';
});

export const selectActiveTitlePoster = createSelector(selectActiveTitle, (title) => title?.thumbnail_link);

export const selectActiveVersion = createSelector(selectTitleV2Slice, (titleSlice) => titleSlice.activeVersion);

export const selectAllTitleMetadata = createSelector(selectTitleV2Slice, (titleSlice) => titleSlice.titleMetadata);

export const selectTitleMetadataById = createSelector(
  selectTitleV2Slice,
  (_: RootState, titleId?: number) => titleId,
  (slice, titleId): TitleMetadataItem | undefined => (titleId ? slice.titleMetadata[titleId] : undefined)
);

export const selectTitleMetadataLoading = createSelector(
  selectTitleV2Slice,
  (titleSlice) => titleSlice.titleMetadataLoading === LoadingStatuses.pending
);
